import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { EntityLink } from "../../components/ui/EntityLink";
import { EntitySearchPicker } from "../../components/ui/EntitySearchPicker";
import { genresApi } from "../../utils/api";

// Where a genre sits on `text_embedding` (its name alone) against `embedding` (what artists,
// events and users average — retrofitted on Wikidata when GENRE_EMBEDDING_RETROFIT is on, #638).

function RankShift({ textRank, embeddingRank }) {
  if (textRank == null || embeddingRank == null) return null;
  const title = `text #${textRank} → embedding #${embeddingRank}`;
  if (textRank === embeddingRank) return <span className="text-xs text-muted-foreground" title={title}>=</span>;
  const closer = embeddingRank < textRank;
  const Icon = closer ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center text-xs ${closer ? "text-green-700" : "text-red-700"}`} title={title}>
      <Icon className="h-3 w-3" />
      {Math.abs(textRank - embeddingRank)}
    </span>
  );
}

const IDLE = { data: null, loading: false, error: null };

// One request per change of `deps`; a superseded one is aborted and its answer dropped.
function useRequest(request, deps, errorMessage) {
  const [state, setState] = useState(IDLE);
  useEffect(() => {
    const controller = new AbortController();
    const run = request({ signal: controller.signal });
    if (!run) {
      setState(IDLE);
      return undefined;
    }
    setState({ ...IDLE, loading: true });
    run
      .then((data) => { if (!controller.signal.aborted) setState({ ...IDLE, data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const detail = err.response?.data?.detail;
        setState({ ...IDLE, error: typeof detail === "string" ? detail : errorMessage });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function Status({ loading, error, label }) {
  if (error) return <p className="text-xs text-destructive">{error}</p>;
  if (!loading) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" /> {label}
    </div>
  );
}

function NeighbourList({ title, rows, emptyLabel, showShift = true }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{title}</p>
      <ol className="space-y-0.5">
        {rows.map((row, i) => (
          <li key={row.id} className="flex items-center gap-2 text-sm">
            <span className="w-5 shrink-0 text-right text-xs text-muted-foreground">{i + 1}</span>
            <EntityLink type="genre" id={row.id} className="flex-1 truncate" title={row.name}>{row.name}</EntityLink>
            <span className="font-mono text-xs text-muted-foreground">{row.score.toFixed(4)}</span>
            {showShift && <RankShift textRank={row.text_rank} embeddingRank={row.embedding_rank} />}
          </li>
        ))}
        {rows.length === 0 && <li className="text-xs text-muted-foreground">{emptyLabel}</li>}
      </ol>
    </div>
  );
}

function Cosine({ label, value, field, a, b }) {
  // A null cosine names its cause: which genre lacks a usable vector in this field.
  const lacking = [a, b].filter((g) => g.missing_vectors.includes(field)).map((g) => g.name);
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-mono text-sm">{value == null ? "—" : value.toFixed(4)}</p>
      {value == null && lacking.length > 0 && (
        <p className="text-xs text-muted-foreground">No usable {field} on {lacking.join(" or ")}</p>
      )}
    </div>
  );
}

function ComparePanel({ genre }) {
  const [other, setOther] = useState(null);
  const { data: result, loading, error } = useRequest(
    (config) => other && genresApi.compare(genre.id, other.id, config), [genre.id, other], "Could not compare",
  );
  // Every cause, not the first: an unlinked genre must not hide the other one's stale link.
  const missing = result?.missing_entities ?? [];
  const noPathReasons = result ? [
    ...[result.a, result.b].filter((g) => !g.wikidata_id).map((g) => `${g.name} has no Wikidata link.`),
    ...(missing.length ? [`${missing.join(" and ")} ${missing.length > 1 ? "are" : "is"} not in the Wikidata catalogue (stale link?).`] : []),
  ] : [];

  return (
    <div className="space-y-2">
      <EntitySearchPicker
        label="Compare with…"
        placeholder="Search genres..."
        // One more than shown: the picker drops the inspected genre itself (excludeId).
        search={async (q, config) => (await genresApi.getAll({ search: q, page_size: 9 }, config)).genres || []}
        optionLabel={(g) => g.name}
        value={other}
        onChange={setOther}
        allowRawId={false}
        excludeId={genre.id}
      />
      <Status loading={loading} error={error} label="Comparing…" />
      {result && !loading && (
        <div className="space-y-2 rounded-md border p-3">
          <div className="grid grid-cols-2 gap-2">
            <Cosine label="Cosine on embedding" value={result.embedding_cosine} field="embedding" a={result.a} b={result.b} />
            <Cosine label="Cosine on text_embedding" value={result.text_embedding_cosine} field="text_embedding" a={result.a} b={result.b} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Wikidata path (subclass of, either direction)</p>
            {result.path ? (
              <p className="text-sm">
                {result.path.map((node, i) => (
                  <React.Fragment key={node.entity_id}>
                    {i > 0 && <span className="text-muted-foreground"> — </span>}
                    <span title={node.entity_id}>{node.name || node.entity_id}</span>
                  </React.Fragment>
                ))}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {noPathReasons.length > 0 ? `No path: ${noPathReasons.join(" ")}` : "Not connected in the Wikidata catalogue."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GenreSpacePanel({ genre }) {
  const { data, loading, error } = useRequest(
    (config) => genresApi.getNeighbours(genre.id, 10, config), [genre.id], "Could not load neighbours",
  );
  // An empty list is this genre lacking the vector, or no other genre having one yet.
  const emptyLabel = (space) =>
    data?.missing_vectors.includes(space) ? "No usable vector on this genre" : "No other genre has this vector yet";

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Nearest genres</p>
      <Status loading={loading} error={error} label="Loading…" />
      {data && (
        <>
          <p className="text-xs text-muted-foreground">
            Retrofit is <strong>{data.retrofit ? "on" : "off"}</strong> on this server.
            {!data.genre.wikidata_id && " No Wikidata link."}
            {data.missing_vectors.length > 0 && ` This genre has no usable ${data.missing_vectors.join(" or ")}.`}
            {data.own_vector_moved === false && !data.identical && (data.retrofit
              ? " Its own vector is still its text vector (unlinked, its entity has no edges, or no sync has run since it was linked), so the differences below come from its neighbours' vectors."
              : " Its own vector is its text vector; the differences below come from its neighbours' stored vectors, which the nightly genre sync restores.")}
            {data.own_vector_moved &&
              ` Its own vector moved: cosine ${data.self_cosine.toFixed(6)} with its text vector.` +
              (data.retrofit ? "" : " The nightly genre sync restores it to the text vector.")}
            {data.missing_vectors.length === 0 && !data.identical && " Arrows: rank change from text_embedding to embedding."}
          </p>
          {data.identical ? (
            <>
              <p className="text-xs text-muted-foreground">Every genre here scores the same on both vectors (to 4 dp).</p>
              {/* No arrows: a gap right at the noise bar can tie two genres in one field and not the other. */}
              <NeighbourList title="embedding and text_embedding" rows={data.embedding} emptyLabel={emptyLabel("embedding")} showShift={false} />
            </>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <NeighbourList title="text_embedding (name alone)" rows={data.text_embedding} emptyLabel={emptyLabel("text_embedding")} />
              <NeighbourList title="embedding (what everything averages)" rows={data.embedding} emptyLabel={emptyLabel("embedding")} />
            </div>
          )}
        </>
      )}
      <ComparePanel genre={genre} />
    </div>
  );
}
